import { useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useUpdateDomainTags, type CDNDomain } from '@/api/manticore';

/** Comma string → unique, trimmed, non-empty tags. */
const parseTags = (s: string) => Array.from(new Set(s.split(',').map((t) => t.trim()).filter(Boolean)));

/**
 * Add tags to the selected CDN domains. Tags are appended (union with each
 * domain's existing tags), then persisted per-domain via PUT /cdn/domains/:id/tags.
 */
export function DomainTagsDialog({
  open,
  onOpenChange,
  domains,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  domains: CDNDomain[];
  onDone: () => void;
}) {
  const { show } = useToast();
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const update = useUpdateDomainTags();
  const count = domains.length;

  const submit = async () => {
    const add = parseTags(input);
    if (!add.length) return;
    setBusy(true);
    try {
      await Promise.all(
        domains.map((d) => {
          const merged = Array.from(new Set([...(d.tags ?? []), ...add]));
          return update.mutateAsync({ domainId: String(d.id), tags: merged });
        }),
      );
      show({ tone: 'success', title: `Tagged ${count} domain${count === 1 ? '' : 's'}` });
      onDone();
      onOpenChange(false);
      setInput('');
    } catch {
      show({ tone: 'danger', title: 'Tagging failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add tags to {count} domain{count === 1 ? '' : 's'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Tags are added to existing ones (comma-separated). Use them to group and filter domains.
            </p>
            <Input
              placeholder="prod, edge, team-a"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoFocus
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!parseTags(input).length || busy} onClick={submit}>Add tags</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
